import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createMuiTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  const toggleTheme = () => {
    setDarkMode((prevMode) => !prevMode);
  };

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: {
          type: darkMode ? "dark" : "light",
          primary: { main: "#1976d2" },
          secondary: { main: "#ff6f00" },
          success: { main: "#2e7d32" },
          warning: { main: "#ed6c02" },
          error: { main: "#d32f2f" },
          background: {
            default: darkMode ? "#121212" : "#f5f5f5",
            paper: darkMode ? "#1e1e1e" : "#ffffff",
          },
        },
        typography: {
          fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
        },
        overrides: {
          MuiPaper: {
            elevation2: {
              boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)",
            },
          },
          MuiButton: {
            containedPrimary: {
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 8,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "0 2px 8px rgba(25, 118, 210, 0.3)",
              },
            },
            outlinedPrimary: {
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 8,
            },
            outlinedSecondary: {
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 8,
            },
          },
          MuiTableHead: {
            root: {
              backgroundColor: darkMode ? "#2a2a2a" : "#f8f9fa",
            },
          },
          MuiTableCell: {
            head: {
              fontWeight: 600,
              fontSize: "0.8rem",
              color: darkMode ? "#b0b0b0" : "#555",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            },
          },
          MuiTab: {
            root: {
              textTransform: "none",
              fontWeight: 500,
            },
          },
          MuiChip: {
            root: {
              fontWeight: 600,
            },
          },
        },
        scrollbarStyles: {
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
            backgroundColor: darkMode ? "#555" : "#d0d0d0",
            borderRadius: "4px",
          },
        },
      }),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
